import { usePageRefreshKey } from '../../../../context/PageRefreshContext';
import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Icon } from '@iconify/react';
import { SOCIETY_PORTAL, SOCIETY_PORTAL_FLATS } from '../../../../Routing/routes';
import { getWingsList, createWing, editWing, deleteWing, bulkUploadWings, clearWingsBulkUploadState } from '../../../../redux/actions/WingsAndFlatsActions/WingsAndFlatsAction';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import BulkUploadModal from './BulkUploadModal';
import Pagination from '../../../../components/common/Pagination';
import axiosInstance from '../../../../axios/axiosInstance';
import apiEndpoints from '../../../../axios/api';
import { formatIstDate } from '../../../../utils/helpers/istDate';

const WING_TYPES = [
    { label: 'Residential', value: 'RESIDENTIAL' },
    { label: 'Commercial', value: 'COMMERCIAL' },
    { label: 'Parking', value: 'PARKING' },
    { label: 'Utility', value: 'UTILITY' },
    { label: 'Mixed', value: 'MIXED' }
];

const WING_STATUS = [
    { label: 'Active', value: 'ACTIVE' },
    { label: 'Under Construction', value: 'UNDER_CONSTRUCTION' },
    { label: 'Inactive', value: 'INACTIVE' },
    { label: 'Demolished', value: 'DEMOLISHED' }
];

// Blank form. base_floor starts empty rather than 0 so the field shows its
// "Enter base floor" hint instead of a zero that reads as entered data; the
// submit handler already coerces an empty value back to 0.
const EMPTY_WING_FORM = {
    number: '',
    title: '',
    wing_type: 'RESIDENTIAL',
    base_floor: '',
    no_of_floors: '',
    no_of_units: '',
    status: 'ACTIVE',
    commencement_date: '',
    construction_cost: '',
    total_builtup_area: '',
    no_of_water_inlets: ''
};

// Occupancy of one wing: a line + bar on the grid cards, a compact cell in the table.
const WingOccupancyBar = ({ o }) => {
    if (!o) return null;
    return (
        <div className="tw-mb-2">
            <div className="tw-flex tw-items-center tw-justify-between tw-text-[11px] tw-text-gray-600">
                <span>
                    <span className="tw-font-semibold tw-text-emerald-600">{o.occupied}</span> occupied ·{' '}
                    <span className="tw-font-semibold tw-text-amber-600">{o.vacant}</span> vacant
                </span>
                <span className="tw-font-medium">{o.occupancy_percent}%</span>
            </div>
            <div className="tw-h-1.5 tw-bg-gray-100 tw-rounded-full tw-overflow-hidden tw-mt-1">
                <div className="tw-h-full tw-bg-emerald-500" style={{ width: `${Math.min(100, o.occupancy_percent)}%` }} />
            </div>
            {o.flats_not_added > 0 && (
                <p className="tw-text-[10px] tw-text-gray-400 tw-mt-0.5">{o.flats_created} of {o.units} flats added</p>
            )}
        </div>
    );
};

const WingOccupancyCell = ({ o }) => {
    if (!o) return '-';
    return (
        <>
            <span className="tw-text-emerald-600 tw-font-semibold">{o.occupied}</span>
            {' / '}
            <span className="tw-text-amber-600 tw-font-semibold">{o.vacant}</span>
            <span className="tw-text-gray-400"> ({o.occupancy_percent}%)</span>
        </>
    );
};

const Wings = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { wingsList, wingsPagination, wingsLoading, wingsCreateLoading, wingsEditLoading, wingsDeleteLoading, wingsBulkUploadLoading, wingsBulkUploadResult } = useSelector(state => state.WingsAndFlatsReducers);
    const societyId = selectedSocietyStorer.getSocietyId();
    const [showBulkUpload, setShowBulkUpload] = useState(false);

    const [viewMode, setViewMode] = useState('grid');
    const [showModal, setShowModal] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    const [searchParams, setSearchParams] = useState({ page: 1, limit: 10, society_id: societyId });

    const [formData, setFormData] = useState(EMPTY_WING_FORM);
    const [editId, setEditId] = useState(null);

    // Opening the form fresh. Previously the Add button only flipped the modal
    // open and relied on the close handler having cleared the last edit, so a
    // half-finished edit could reappear under the Add New Wing heading.
    const handleOpenAdd = () => {
        setFormData(EMPTY_WING_FORM);
        setEditId(null);
        setShowModal(true);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const wings = wingsList || [];

    // Fetch wings on mount and when search params change
    const refreshKey = usePageRefreshKey();
    useEffect(() => {
        dispatch(getWingsList(searchParams));
    }, [dispatch, searchParams, refreshKey]);

    // Occupied / vacant per wing and in total, from the server (see
    // wingOccupancyService). Follows the list filters, and reloads whenever the
    // list does (after add / edit / delete / upload).
    const [occupancy, setOccupancy] = useState(null);
    useEffect(() => {
        if (!societyId) return;
        const params = { society_id: societyId };
        if (searchQuery) params.search = searchQuery;
        if (filterType !== 'all') params.wing_type = filterType;
        if (filterStatus !== 'all') params.status = filterStatus;
        axiosInstance.get(apiEndpoints.SOCIETY_GET_WING_OCCUPANCY_API, { params })
            .then(res => setOccupancy(res?.data || null))
            .catch(() => setOccupancy(null));
    }, [societyId, searchQuery, filterType, filterStatus, wingsList, refreshKey]);
    const occupancyByWing = useMemo(
        () => new Map((occupancy?.wings || []).map(w => [w.wing_id, w])),
        [occupancy]
    );

    // Update search params when filters change
    useEffect(() => {
        const newParams = {
            page: 1,
            limit: searchParams.limit,
            society_id: societyId
        };
        if (searchQuery) newParams.search = searchQuery;
        if (filterType !== 'all') newParams.wing_type = filterType;
        if (filterStatus !== 'all') newParams.status = filterStatus;

        setSearchParams(newParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery, filterType, filterStatus, societyId]);

    // Units are each wing's configured units (or the flats created, if more);
    // a unit is occupied when an owner or a current tenant / family member is on
    // it. The cards used to divide by the flats created so far while the wings
    // showed their configured units, so totals and percentages didn't match.
    const stats = useMemo(() => {
        const t = occupancy?.totals || {};
        return {
            totalWings: t.wings ?? (wingsPagination?.totalItems || wings.length),
            totalUnits: t.units ?? wings.reduce((sum, w) => sum + (w.no_of_units || 0), 0),
            totalOccupied: t.occupied ?? 0,
            totalVacant: t.vacant ?? 0,
            flatsNotAdded: t.flats_not_added ?? 0,
            occupancyRate: t.occupancy_percent ?? 0,
            vacancyRate: t.vacancy_percent ?? 0,
            totalFloors: wings.reduce((sum, w) => sum + (w.no_of_floors || 0), 0),
        };
    }, [occupancy, wings, wingsPagination]);

    const getTypeColor = (type) => {
        switch (type) {
            case 'COMMERCIAL': return 'tw-bg-amber-500 tw-text-white';
            case 'MIXED': return 'tw-bg-purple-500 tw-text-white';
            case 'PARKING': return 'tw-bg-blue-500 tw-text-white';
            case 'UTILITY': return 'tw-bg-gray-500 tw-text-white';
            default: return 'tw-bg-emerald-500 tw-text-white';
        }
    };

    const getTypeLabel = (type) => {
        const wingType = WING_TYPES.find(t => t.value === type);
        return wingType?.label || type;
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'ACTIVE': return 'tw-bg-green-100 tw-text-green-700';
            case 'UNDER_CONSTRUCTION': return 'tw-bg-yellow-100 tw-text-yellow-700';
            case 'INACTIVE': return 'tw-bg-gray-100 tw-text-gray-700';
            case 'DEMOLISHED': return 'tw-bg-red-100 tw-text-red-700';
            default: return 'tw-bg-gray-100 tw-text-gray-700';
        }
    };

    const getStatusLabel = (status) => {
        const wingStatus = WING_STATUS.find(s => s.value === status);
        return wingStatus?.label || status;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const payload = {
            ...formData,
            society_id: societyId,
            number: parseInt(formData.number),
            base_floor: parseInt(formData.base_floor) || 0,
            no_of_floors: parseInt(formData.no_of_floors),
            no_of_units: parseInt(formData.no_of_units),
            construction_cost: formData.construction_cost !== '' ? parseFloat(formData.construction_cost) : null,
            total_builtup_area: formData.total_builtup_area !== '' ? parseFloat(formData.total_builtup_area) : null,
            no_of_water_inlets: formData.no_of_water_inlets !== '' ? parseInt(formData.no_of_water_inlets) : 0,
            onSuccess: () => {
                handleCloseModal();
                dispatch(getWingsList(searchParams));
            }
        };

        if (editId) {
            dispatch(editWing({ id: editId, ...payload }));
        } else {
            dispatch(createWing(payload));
        }
    };

    const handleEdit = (item) => {
        setFormData({
            number: item.number || '',
            title: item.title || '',
            wing_type: item.wing_type || 'RESIDENTIAL',
            base_floor: item.base_floor != null ? item.base_floor : '',
            no_of_floors: item.no_of_floors || '',
            no_of_units: item.no_of_units || '',
            status: item.status || 'ACTIVE',
            commencement_date: item.commencement_date ? item.commencement_date.split('T')[0] : '',
            construction_cost: item.construction_cost ? parseFloat(item.construction_cost) : '',
            total_builtup_area: item.total_builtup_area ? parseFloat(item.total_builtup_area) : '',
            no_of_water_inlets: item.no_of_water_inlets != null ? item.no_of_water_inlets : ''
        });
        setEditId(item.wing_id);
        setShowModal(true);
    };

    const handleDelete = (wingId) => {
        if (window.confirm('Are you sure you want to delete this wing?')) {
            dispatch(deleteWing({
                id: wingId,
                society_id: societyId,
                onSuccess: () => {
                    dispatch(getWingsList(searchParams));
                }
            }));
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setFormData(EMPTY_WING_FORM);
        setEditId(null);
    };

    const handleManageFlats = (wing) => {
        navigate(`${SOCIETY_PORTAL}${SOCIETY_PORTAL_FLATS}?wingId=${wing.wing_id}&wingName=${encodeURIComponent(wing.title)}`);
    };

    const clearFilters = () => {
        setSearchQuery('');
        setFilterType('all');
        setFilterStatus('all');
    };

    const hasActiveFilters = searchQuery || filterType !== 'all' || filterStatus !== 'all';

    const handlePageChange = (newPage) => {
        setSearchParams({ ...searchParams, page: newPage });
    };
    // Reset to page 1 whenever the page size changes — otherwise the current
    // page number can end up past totalPages and the list looks empty.
    const handleItemsPerPageChange = (newLimit) => {
        setSearchParams({ ...searchParams, page: 1, limit: newLimit });
    };

    return (
        <>
            {/* Header */}
            <div className="tw-flex tw-flex-col tw-gap-3 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between tw-mb-4">
                <div>
                    <h1 className="tw-text-lg tw-font-semibold tw-text-gray-800">Wings Management</h1>
                    <p className="tw-text-xs tw-text-gray-500">Manage society wings</p>
                </div>
                <div className="tw-flex tw-items-center tw-gap-2">
                    <button
                        onClick={() => { dispatch(clearWingsBulkUploadState()); setShowBulkUpload(true); }}
                        className="tw-flex tw-flex-1 sm:tw-flex-none tw-items-center tw-justify-center tw-gap-1.5 tw-whitespace-nowrap tw-text-emerald-700 tw-border tw-border-emerald-200 tw-bg-emerald-50 tw-px-3 tw-py-1.5 tw-rounded-md tw-text-sm tw-font-medium hover:tw-bg-emerald-100"
                    >
                        <Icon icon="mdi:file-excel-outline" />
                        Upload Excel
                    </button>
                    <button
                        onClick={handleOpenAdd}
                        className="tw-flex tw-flex-1 sm:tw-flex-none tw-items-center tw-justify-center tw-gap-1.5 tw-whitespace-nowrap tw-bg-blue-600 tw-text-white tw-px-3 tw-py-1.5 tw-rounded-md tw-text-sm tw-font-medium hover:tw-bg-blue-700"
                    >
                        <Icon icon="mdi:plus" />
                        Add Wing
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-3 md:tw-grid-cols-5 tw-gap-3 tw-mb-4">
                {[
                    { icon: 'mdi:office-building', color: 'tw-bg-blue-500', value: stats.totalWings, label: 'Total Wings' },
                    { icon: 'mdi:check-circle', color: 'tw-bg-emerald-500', value: stats.totalOccupied, label: 'Occupied', sub: `${stats.occupancyRate}% occupancy` },
                    { icon: 'mdi:home-outline', color: 'tw-bg-amber-500', value: stats.totalVacant, label: 'Vacant', sub: `${stats.vacancyRate}% vacant`, subClass: 'tw-text-amber-600' },
                    { icon: 'mdi:layers-triple', color: 'tw-bg-pink-500', value: stats.totalFloors, label: 'Total Floors' },
                    { icon: 'mdi:door', color: 'tw-bg-purple-500', value: stats.totalUnits, label: 'Total Units', sub: stats.flatsNotAdded ? `${stats.flatsNotAdded} flats not added yet` : null, subClass: 'tw-text-gray-500' },
                ].map((stat, i) => (
                    <div key={i} className="tw-bg-white tw-rounded-lg tw-px-3 tw-py-2.5 tw-shadow-sm tw-border tw-border-gray-100 tw-flex tw-items-center">
                        <div className="tw-flex tw-items-center tw-gap-2">
                            <div className={`tw-w-9 tw-h-9 tw-rounded-lg ${stat.color} tw-flex tw-items-center tw-justify-center tw-flex-shrink-0`}>
                                <Icon icon={stat.icon} className="tw-text-white tw-text-base" />
                            </div>
                            <div className="tw-min-w-0">
                                <div className="tw-flex tw-items-baseline tw-gap-1">
                                    <span className="tw-text-base tw-font-bold tw-text-gray-800">{stat.value}</span>
                                    <span className="tw-text-[11px] tw-text-gray-500 tw-truncate">{stat.label}</span>
                                </div>
                                {stat.sub && <p className={`tw-text-[10px] tw-leading-tight ${stat.subClass || 'tw-text-emerald-600'}`}>{stat.sub}</p>}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Filter Bar */}
            <div className="tw-bg-white tw-rounded-lg tw-py-2.5 tw-px-3 tw-mb-4 tw-shadow-sm tw-border tw-border-gray-100 tw-flex tw-items-center tw-gap-2.5 tw-flex-wrap">
                <div className="tw-flex-1 tw-min-w-[200px] tw-relative">
                    <Icon icon="mdi:magnify" className="tw-absolute tw-left-2.5 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 tw-text-sm" />
                    <input
                        type="text"
                        placeholder="Search wings..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="tw-w-full tw-pl-8 tw-pr-2 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-border-blue-500"
                    />
                </div>
                <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-border-blue-500"
                >
                    <option value="all">All Types</option>
                    {WING_TYPES.map(type => <option key={type.value} value={type.value}>{type.label}</option>)}
                </select>
                <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-border-blue-500"
                >
                    <option value="all">All Status</option>
                    {WING_STATUS.map(status => <option key={status.value} value={status.value}>{status.label}</option>)}
                </select>
                {hasActiveFilters && (
                    <button onClick={clearFilters} className="tw-flex tw-items-center tw-gap-1 tw-text-red-500 tw-text-xs tw-font-medium hover:tw-text-red-600">
                        <Icon icon="mdi:close" className="tw-text-sm" /> Clear
                    </button>
                )}
                <div className="tw-flex tw-bg-gray-100 tw-rounded-lg tw-p-1">
                    <button
                        onClick={() => setViewMode('grid')}
                        className={`tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-md tw-text-sm tw-font-medium tw-transition-all ${viewMode === 'grid' ? 'tw-bg-white tw-text-blue-600 tw-shadow-sm' : 'tw-text-gray-500 hover:tw-text-gray-700'}`}
                    >
                        <Icon icon="mdi:view-grid" className="tw-text-base" />
                        <span className="tw-hidden sm:tw-inline">Grid</span>
                    </button>
                    <button
                        onClick={() => setViewMode('list')}
                        className={`tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-md tw-text-sm tw-font-medium tw-transition-all ${viewMode === 'list' ? 'tw-bg-white tw-text-blue-600 tw-shadow-sm' : 'tw-text-gray-500 hover:tw-text-gray-700'}`}
                    >
                        <Icon icon="mdi:view-list" className="tw-text-base" />
                        <span className="tw-hidden sm:tw-inline">List</span>
                    </button>
                </div>
                <span className="tw-text-[11px] tw-text-gray-400 tw-ml-auto">
                    Showing {wings.length} of {wingsPagination?.totalItems || wings.length}
                </span>
            </div>

            {/* Loading State */}
            {wingsLoading && (
                <div className="tw-flex tw-justify-center tw-items-center tw-py-10">
                    <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b-2 tw-border-blue-600"></div>
                </div>
            )}

            {/* Grid View */}
            {!wingsLoading && viewMode === 'grid' && (
                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4 tw-gap-4">
                    {wings.map((wing) => (
                        <div key={wing.wing_id} className="tw-bg-white tw-rounded-xl tw-shadow-sm tw-border tw-border-gray-100 tw-overflow-hidden hover:tw-shadow-md tw-transition-shadow tw-flex tw-flex-col">
                            {/* Card Header */}
                            <div className="tw-p-4 tw-pb-3">
                                <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
                                    <div className="tw-flex tw-items-center tw-gap-3">
                                        <div className="tw-w-10 tw-h-10 tw-rounded-lg tw-bg-blue-500 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                            <Icon icon="mdi:office-building" className="tw-text-white tw-text-lg" />
                                        </div>
                                        <div className="tw-min-w-0">
                                            <h4 className="tw-font-semibold tw-text-gray-800 tw-text-sm tw-truncate">{wing.title}</h4>
                                            <p className="tw-text-xs tw-text-gray-500">{wing.no_of_floors} Floors • {wing.no_of_units} Units</p>
                                        </div>
                                    </div>
                                    <span className={`tw-px-2.5 tw-py-1 tw-rounded-full tw-text-[10px] tw-font-semibold tw-whitespace-nowrap ${getTypeColor(wing.wing_type)}`}>
                                        {getTypeLabel(wing.wing_type)}
                                    </span>
                                </div>
                            </div>

                            {/* Card Body */}
                            <div className="tw-px-4 tw-pb-3 tw-flex-1">
                                <WingOccupancyBar o={occupancyByWing.get(wing.wing_id)} />
                                <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
                                    <span className={`tw-px-2 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-medium ${getStatusColor(wing.status)}`}>
                                        {getStatusLabel(wing.status)}
                                    </span>
                                    <span className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-500">
                                        <Icon icon="mdi:layers-triple" className="tw-text-gray-400 tw-text-sm" /> Base: {wing.base_floor}
                                    </span>
                                </div>
                                {wing.commencement_date && (
                                    <div className="tw-mt-2 tw-text-xs tw-text-gray-500 tw-flex tw-items-center tw-gap-1">
                                        <Icon icon="mdi:calendar" className="tw-text-gray-400 tw-text-sm" />
                                        Started: {formatIstDate(wing.commencement_date)}
                                    </div>
                                )}
                                {(wing.construction_cost || wing.total_builtup_area || wing.no_of_water_inlets > 0) && (
                                    <div className="tw-mt-2 tw-flex tw-flex-wrap tw-gap-x-3 tw-gap-y-1 tw-text-xs tw-text-gray-500">
                                        {wing.construction_cost && (
                                            <span className="tw-flex tw-items-center tw-gap-1">
                                                <Icon icon="mdi:currency-inr" className="tw-text-gray-400 tw-text-sm" />
                                                {parseFloat(wing.construction_cost).toLocaleString('en-IN')}
                                            </span>
                                        )}
                                        {wing.total_builtup_area && (
                                            <span className="tw-flex tw-items-center tw-gap-1">
                                                <Icon icon="mdi:floor-plan" className="tw-text-gray-400 tw-text-sm" />
                                                {parseFloat(wing.total_builtup_area).toLocaleString()} sq ft
                                            </span>
                                        )}
                                        {wing.no_of_water_inlets > 0 && (
                                            <span className="tw-flex tw-items-center tw-gap-1">
                                                <Icon icon="mdi:water" className="tw-text-gray-400 tw-text-sm" />
                                                {wing.no_of_water_inlets} inlets
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Card Footer */}
                            <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-bg-gray-50 tw-border-t tw-border-gray-100">
                                <button onClick={() => handleManageFlats(wing)} className="tw-text-xs tw-text-blue-600 tw-font-medium hover:tw-text-blue-700 tw-flex tw-items-center tw-gap-1.5">
                                    <Icon icon="mdi:door-open" className="tw-text-sm" /> View Flats
                                </button>
                                <div className="tw-flex tw-items-center tw-gap-1">
                                    <button onClick={() => handleManageFlats(wing)} className="tw-p-1.5 tw-rounded-md hover:tw-bg-white tw-text-gray-400 hover:tw-text-blue-600 tw-transition-colors" title="View">
                                        <Icon icon="mdi:eye-outline" className="tw-text-base" />
                                    </button>
                                    <button onClick={() => handleEdit(wing)} className="tw-p-1.5 tw-rounded-md hover:tw-bg-white tw-text-gray-400 hover:tw-text-blue-600 tw-transition-colors" title="Edit">
                                        <Icon icon="mdi:pencil-outline" className="tw-text-base" />
                                    </button>
                                    <button onClick={() => handleDelete(wing.wing_id)} className="tw-p-1.5 tw-rounded-md hover:tw-bg-white tw-text-gray-400 hover:tw-text-red-500 tw-transition-colors" title="Delete" disabled={wingsDeleteLoading}>
                                        <Icon icon="mdi:trash-can-outline" className="tw-text-base" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* List View */}
            {!wingsLoading && viewMode === 'list' && (
                <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-overflow-hidden">
                    <table className="tw-w-full tw-text-sm">
                        <thead>
                            <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-100">
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Wing</th>
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Type</th>
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Status</th>
                                <th className="tw-text-center tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Floors</th>
                                <th className="tw-text-center tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Units</th>
                                <th className="tw-text-center tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Occupied / Vacant</th>
                                <th className="tw-text-center tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Base Floor</th>
                                <th className="tw-text-right tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Construction Cost</th>
                                <th className="tw-text-right tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Built-up Area</th>
                                <th className="tw-text-center tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Water Inlets</th>
                                <th className="tw-text-center tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {wings.map((wing) => (
                                <tr key={wing.wing_id} className="tw-border-b tw-border-gray-50 hover:tw-bg-gray-50">
                                    <td className="tw-px-3 tw-py-1.5">
                                        <div className="tw-flex tw-items-center tw-gap-2">
                                            <div className="tw-w-7 tw-h-7 tw-rounded-md tw-bg-blue-500 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                                <Icon icon="mdi:office-building" className="tw-text-white tw-text-sm" />
                                            </div>
                                            <div className="tw-min-w-0 tw-leading-tight">
                                                <h4 className="tw-font-semibold tw-text-gray-800 tw-text-sm tw-truncate">{wing.title}</h4>
                                                <p className="tw-text-[10px] tw-text-gray-500 tw-leading-none tw-mt-0.5">{wing.no_of_floors} Floors • {wing.no_of_units} Units</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="tw-px-3 tw-py-1.5">
                                        <span className={`tw-px-2 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-medium ${getTypeColor(wing.wing_type)}`}>
                                            {getTypeLabel(wing.wing_type)}
                                        </span>
                                    </td>
                                    <td className="tw-px-3 tw-py-1.5">
                                        <span className={`tw-px-2 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-medium ${getStatusColor(wing.status)}`}>
                                            {getStatusLabel(wing.status)}
                                        </span>
                                    </td>
                                    <td className="tw-px-3 tw-py-1.5 tw-text-center tw-text-gray-600">{wing.no_of_floors}</td>
                                    <td className="tw-px-3 tw-py-1.5 tw-text-center tw-font-medium tw-text-gray-800">{wing.no_of_units}</td>
                                    <td className="tw-px-3 tw-py-1.5 tw-text-center tw-text-xs tw-whitespace-nowrap">
                                        <WingOccupancyCell o={occupancyByWing.get(wing.wing_id)} />
                                    </td>
                                    <td className="tw-px-3 tw-py-1.5 tw-text-center tw-text-gray-500">{wing.base_floor}</td>
                                    <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-600">{wing.construction_cost ? parseFloat(wing.construction_cost).toLocaleString('en-IN') : '-'}</td>
                                    <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-600">{wing.total_builtup_area ? `${parseFloat(wing.total_builtup_area).toLocaleString()} sq ft` : '-'}</td>
                                    <td className="tw-px-3 tw-py-1.5 tw-text-center tw-text-gray-600">{wing.no_of_water_inlets || 0}</td>
                                    <td className="tw-px-3 tw-py-1.5">
                                        <div className="tw-flex tw-items-center tw-justify-center tw-gap-1">
                                            <button onClick={() => handleManageFlats(wing)} className="tw-p-1.5 tw-rounded hover:tw-bg-blue-50 tw-text-gray-400 hover:tw-text-blue-600" title="View Flats">
                                                <Icon icon="mdi:eye-outline" />
                                            </button>
                                            <button onClick={() => handleEdit(wing)} className="tw-p-1.5 tw-rounded hover:tw-bg-gray-100 tw-text-gray-400" title="Edit">
                                                <Icon icon="mdi:pencil-outline" />
                                            </button>
                                            <button onClick={() => handleDelete(wing.wing_id)} className="tw-p-1.5 tw-rounded hover:tw-bg-red-50 tw-text-gray-400 hover:tw-text-red-500" title="Delete" disabled={wingsDeleteLoading}>
                                                <Icon icon="mdi:trash-can-outline" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {wings.length === 0 && (
                        <div className="tw-text-center tw-py-8">
                            <Icon icon="mdi:office-building-outline" className="tw-text-3xl tw-text-gray-300" />
                            <p className="tw-text-gray-500 tw-mt-2 tw-text-sm">No wings found</p>
                        </div>
                    )}
                </div>
            )}

            {/* Pagination — shared component with page-size selector,
                "N of M" info, and jump-to-first/last controls. Renders even
                on a single-page result so users can adjust rows per page. */}
            {!wingsLoading && wingsPagination && (
                <div className="tw-mt-4">
                    <Pagination
                        currentPage={wingsPagination.page || 1}
                        totalPages={wingsPagination.totalPages || 1}
                        totalItems={wingsPagination.totalItems || wings.length}
                        itemsPerPage={searchParams.limit}
                        onPageChange={handlePageChange}
                        onItemsPerPageChange={handleItemsPerPageChange}
                        itemsPerPageOptions={[10, 20, 50, 100]}
                        itemLabel="wings"
                    />
                </div>
            )}

            {/* Modal */}
            {showModal && (
                <div className="tw-fixed tw-inset-0 tw-bg-black/40 tw-flex tw-items-center tw-justify-center tw-z-50 tw-p-4">
                    <div className="tw-bg-white tw-rounded-xl tw-w-full tw-max-w-md tw-shadow-xl">
                        <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-3 tw-border-b tw-border-gray-100">
                            <div className="tw-flex tw-items-center tw-gap-2.5">
                                <div className="tw-w-8 tw-h-8 tw-rounded-lg tw-bg-blue-500 tw-flex tw-items-center tw-justify-center">
                                    <Icon icon="mdi:office-building" className="tw-text-white tw-text-sm" />
                                </div>
                                <h3 className="tw-font-semibold tw-text-gray-800 tw-text-sm">{editId ? 'Edit Wing' : 'Add New Wing'}</h3>
                            </div>
                            <button onClick={handleCloseModal} className="tw-p-1 tw-rounded-full hover:tw-bg-gray-100 tw-transition-colors">
                                <Icon icon="mdi:close" className="tw-text-base tw-text-gray-400" />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="tw-p-5">
                            <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                                <div>
                                    <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Wing Number <span className="tw-text-red-500">*</span></label>
                                    <input
                                        type="number"
                                        value={formData.number}
                                        onChange={(e) => setFormData({ ...formData, number: e.target.value })}
                                        className="tw-w-full tw-px-3 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-border-blue-500"
                                        placeholder="Enter wing no"
                                        min="1"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Wing Title <span className="tw-text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        value={formData.title}
                                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                        className="tw-w-full tw-px-3 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-border-blue-500"
                                        placeholder="Enter wing title"
                                        maxLength={100}
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Wing Type <span className="tw-text-red-500">*</span></label>
                                    <SearchableDropdown
                                        value={formData.wing_type}
                                        onChange={(val) => setFormData({ ...formData, wing_type: val })}
                                        options={WING_TYPES}
                                        placeholder="Select Wing Type"
                                        clearable={false}
                                    />
                                </div>
                                <div>
                                    <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Status</label>
                                    <SearchableDropdown
                                        value={formData.status}
                                        onChange={(val) => setFormData({ ...formData, status: val })}
                                        options={WING_STATUS}
                                        placeholder="Select Status"
                                        clearable={false}
                                    />
                                </div>
                                <div>
                                    <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Base Floor</label>
                                    <input
                                        type="number"
                                        value={formData.base_floor}
                                        onChange={(e) => setFormData({ ...formData, base_floor: e.target.value })}
                                        className="tw-w-full tw-px-3 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-border-blue-500"
                                        placeholder="Enter base floor"
                                    />
                                </div>
                                <div>
                                    <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">No. of Floors <span className="tw-text-red-500">*</span></label>
                                    <input
                                        type="number"
                                        value={formData.no_of_floors}
                                        onChange={(e) => setFormData({ ...formData, no_of_floors: e.target.value })}
                                        className="tw-w-full tw-px-3 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-border-blue-500"
                                        placeholder="Enter no of floors"
                                        min="1"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">No. of Units <span className="tw-text-red-500">*</span></label>
                                    <input
                                        type="number"
                                        value={formData.no_of_units}
                                        onChange={(e) => setFormData({ ...formData, no_of_units: e.target.value })}
                                        className="tw-w-full tw-px-3 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-border-blue-500"
                                        placeholder="Enter no of flats"
                                        min="1"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Commencement Date <span className="tw-text-red-500">*</span></label>
                                    <input
                                        type="date"
                                        value={formData.commencement_date}
                                        onChange={(e) => setFormData({ ...formData, commencement_date: e.target.value })}
                                        className="tw-w-full tw-px-3 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-border-blue-500"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Construction Cost</label>
                                    <input
                                        type="number"
                                        value={formData.construction_cost}
                                        onChange={(e) => setFormData({ ...formData, construction_cost: e.target.value })}
                                        className="tw-w-full tw-px-3 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-border-blue-500"
                                        placeholder="Enter construction cost"
                                        step="0.01"
                                        min="0"
                                    />
                                </div>
                                <div>
                                    <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Total Built-up Area (sq ft)</label>
                                    <input
                                        type="number"
                                        value={formData.total_builtup_area}
                                        onChange={(e) => setFormData({ ...formData, total_builtup_area: e.target.value })}
                                        className="tw-w-full tw-px-3 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-border-blue-500"
                                        placeholder="Enter built-up area"
                                        step="0.01"
                                        min="0"
                                    />
                                </div>
                                <div>
                                    <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">No. of Water Inlets</label>
                                    <input
                                        type="number"
                                        value={formData.no_of_water_inlets}
                                        onChange={(e) => setFormData({ ...formData, no_of_water_inlets: e.target.value })}
                                        className="tw-w-full tw-px-3 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-border-blue-500"
                                        placeholder="Enter no of water inlets"
                                        min="0"
                                        step="1"
                                    />
                                </div>
                            </div>
                            <div className="tw-flex tw-justify-end tw-gap-2 tw-mt-4 tw-pt-4 tw-border-t tw-border-gray-100">
                                <button type="button" onClick={handleCloseModal} className="tw-px-4 tw-py-1.5 tw-text-sm tw-font-medium tw-text-gray-600 hover:tw-text-gray-800">
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="tw-px-4 tw-py-1.5 tw-bg-blue-600 tw-text-white tw-rounded-md tw-text-sm tw-font-medium hover:tw-bg-blue-700 disabled:tw-opacity-50 tw-flex tw-items-center tw-gap-2"
                                    disabled={wingsCreateLoading || wingsEditLoading}
                                >
                                    {(wingsCreateLoading || wingsEditLoading) && (
                                        <div className="tw-animate-spin tw-rounded-full tw-h-3 tw-w-3 tw-border-b-2 tw-border-white"></div>
                                    )}
                                    {editId ? 'Update' : 'Add Wing'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Bulk Upload Modal */}
            {showBulkUpload && (
                <BulkUploadModal
                    title="Bulk Upload Wings"
                    description="Upload an Excel file to create multiple wings at once"
                    templateName="wings_bulk_upload_sample.xlsx"
                    templateHeaders={['number', 'title', 'wing_type', 'status', 'base_floor', 'no_of_floors', 'no_of_units', 'commencement_date', 'construction_cost', 'total_builtup_area', 'no_of_water_inlets']}
                    templateSample={[1, 'A Wing', 'RESIDENTIAL', 'ACTIVE', 0, 10, 40, '2024-01-15', 5000000, 50000, 4]}
                    templateNotes={['', '', 'RESIDENTIAL / COMMERCIAL / PARKING / UTILITY / MIXED', 'ACTIVE / UNDER_CONSTRUCTION / INACTIVE / DEMOLISHED', '', '', '', 'YYYY-MM-DD', '', '', '']}
                    onClose={() => { setShowBulkUpload(false); dispatch(clearWingsBulkUploadState()); }}
                    onUpload={(file) => dispatch(bulkUploadWings({
                        society_id: societyId,
                        file,
                        onSuccess: () => dispatch(getWingsList(searchParams)),
                    }))}
                    uploading={wingsBulkUploadLoading}
                    result={wingsBulkUploadResult}
                />
            )}
        </>
    );
};

export default Wings;
